/**
 * Screen edge glow effect for consequence feedback
 * Per design brief: subtle, professional feedback without gamification
 */

import { Container, Graphics } from 'pixi.js';
import { theme } from '@core/theme';
import type { ConsequenceSeverity } from '@core/types';

export class ScreenGlow extends Container {
  private glowGraphics: Graphics;
  private currentAlpha = 0;
  private currentColor: number;
  private animationFrame: number | null = null;
  private glowWidth: number;
  private glowHeight: number;

  constructor(width: number, height: number) {
    super();

    this.glowWidth = width;
    this.glowHeight = height;
    this.currentColor = theme.colors.safe;

    this.glowGraphics = new Graphics();
    this.addChild(this.glowGraphics);

    // Start invisible
    this.alpha = 0;
  }

  /**
   * Show glow effect based on consequence severity
   */
  show(severity: ConsequenceSeverity): Promise<void> {
    return new Promise((resolve) => {
      const config = this.getGlowConfig(severity);
      this.currentColor = config.color;

      this.drawGlow();

      this.animateAlpha(0, config.maxAlpha, config.fadeInDuration, () => {
        setTimeout(() => {
          this.animateAlpha(config.maxAlpha, 0, config.fadeOutDuration, () => {
            resolve();
          });
        }, config.holdDuration);
      });
    });
  }

  /**
   * Pulse effect for near-miss outcomes
   */
  pulse(count = 2): Promise<void> {
    return new Promise((resolve) => {
      this.currentColor = theme.glowColors.nearMiss;
      this.drawGlow();

      let pulseCount = 0;
      const doPulse = () => {
        this.animateAlpha(0, 0.4, 150, () => {
          this.animateAlpha(0.4, 0, 150, () => {
            pulseCount++;
            if (pulseCount < count) {
              setTimeout(doPulse, 100);
            } else {
              resolve();
            }
          });
        });
      };

      doPulse();
    });
  }

  private getGlowConfig(severity: ConsequenceSeverity) {
    switch (severity) {
      case 'positive':
        return {
          color: theme.glowColors.safe,
          maxAlpha: 0.3,
          fadeInDuration: 500,
          holdDuration: 1000,
          fadeOutDuration: 500,
        };
      case 'warning':
        return {
          color: theme.glowColors.nearMiss,
          maxAlpha: 0.4,
          fadeInDuration: 300,
          holdDuration: 600,
          fadeOutDuration: 400,
        };
      case 'negative':
        return {
          color: theme.glowColors.unsafe,
          maxAlpha: 0.5,
          fadeInDuration: 400,
          holdDuration: 0,
          fadeOutDuration: 600,
        };
      default:
        return {
          color: theme.colors.neutral,
          maxAlpha: 0.2,
          fadeInDuration: 400,
          holdDuration: 500,
          fadeOutDuration: 400,
        };
    }
  }

  private drawGlow(): void {
    this.glowGraphics.clear();

    const glowSize = 60;

    // Top edge
    this.glowGraphics.rect(0, 0, this.glowWidth, glowSize);
    this.glowGraphics.fill({ color: this.currentColor, alpha: 1 });

    // Bottom edge
    this.glowGraphics.rect(0, this.glowHeight - glowSize, this.glowWidth, glowSize);
    this.glowGraphics.fill({ color: this.currentColor, alpha: 1 });

    // Left edge
    this.glowGraphics.rect(0, glowSize, glowSize, this.glowHeight - glowSize * 2);
    this.glowGraphics.fill({ color: this.currentColor, alpha: 1 });

    // Right edge
    this.glowGraphics.rect(this.glowWidth - glowSize, glowSize, glowSize, this.glowHeight - glowSize * 2);
    this.glowGraphics.fill({ color: this.currentColor, alpha: 1 });

    this.glowGraphics.alpha = 0.6;
  }

  private animateAlpha(
    from: number,
    to: number,
    duration: number,
    onComplete?: () => void
  ): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }

    const startTime = performance.now();
    this.currentAlpha = from;
    this.alpha = from;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const eased = 1 - (1 - progress) * (1 - progress);

      this.currentAlpha = from + (to - from) * eased;
      this.alpha = this.currentAlpha;

      if (progress < 1) {
        this.animationFrame = requestAnimationFrame(animate);
      } else {
        this.alpha = to;
        this.animationFrame = null;
        onComplete?.();
      }
    };

    this.animationFrame = requestAnimationFrame(animate);
  }

  resize(width: number, height: number): void {
    this.glowWidth = width;
    this.glowHeight = height;
    this.drawGlow();
  }

  hide(): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    this.alpha = 0;
  }
}
